import { showToast } from '../../../components/Toast'
import { videoIntentProblem } from './referenceLimits'
import {
	generateImageAsync,
	generateMidjourney,
	generateMidjourneyVideo,
	generateVideo,
	imageToImage,
	type TaskStatusResponse,
} from '../../../api/images'
import {
	generateMusicAsync,
	generateSounds,
	generateSoundtrack,
	extendTrack,
	uploadCover,
	addVocals,
	addInstrumental,
	referenceGenerate,
	type MusicGenerateResponse,
	type SunoTrack,
} from '../../../api/music'
import type { AudioOperation } from '../../../context/GenerateContext'
import { providerOf } from './trackOperations'
import { isTtsModelId } from './ttsModels'
import { generateSpeech } from '../../../api/voice'
import { getSlidesFolder } from '../../../api/slidesFolderTracker'
import { hfPayloadFromSettings } from './gammaHf'
import type { Model } from '../../../data/models'
import type { GenerateSettings } from '../hooks/useGenerateSettings'
import type { UploadedFile, UploadedVideo } from './uploadHelpers'
import { PROVIDER, TASK_TYPE } from './constants'
import { waitForTask } from './waitForTask'
import {
	buildFluxPayload,
	buildImagePayload,
	buildKlingPayload,
	isKling3Model,
	buildMidjourneyI2IPayload,
	buildMidjourneyPayload,
	buildMidjourneyVideoPayload,
	buildVideoPayload,
	getImageInput,
} from './generatePayloadBuilders'

// Аудио-генерация идёт дольше обычной — 2-3 минуты: спрашиваем не чаще раза в 10 с.
const AUDIO_POLL_INTERVAL_MS = 10000

// Конвертирует ID Suno модели из формата бэкенда в формат API /music/generate-async
// 'suno-v4_5all' → 'V4_5ALL'
function sunoModelApiId(modelId: string): string {
	return modelId.replace(/^suno-/, '').toUpperCase()
}

// ── API response types ───────────────────────────────────────
interface ApiResponse {
	ok: boolean
	error?: string
	request_id?: string
	/** Подпись сервера к запуску: длина по референсу, перезапуск как редактирование и т.п. */
	notice?: string
	image_url?: string
	video_url?: string
	/** Отказ нейросети, закончившийся предупреждением или списанием («Отказы нейросетей»): текст окна. */
	refusal?: { title: string | null; action: 'warn' | 'charge'; text: string | null; credits: number } | null
}

export interface GenerationResult {
	ok: boolean
	resultUrl?: string
	resultUrls?: string[]       // для аудио — массив из 2 треков
	/** Для аудио — обложки треков параллельно resultUrls (index-aligned).
	 *  Первый элемент используется как previewUrl карточки. */
	imageUrls?: string[]
	requestId?: string
	error?: string
	isNsfwWarning?: boolean
}

/**
 * Выбирает название для параметрической audio-операции (add_vocals /
 * add_instrumental / upload_cover). Suno отклоняет запросы с пустым title
 * («title cannot be null»), поэтому если юзер не заполнил — берём имя
 * source-трека, иначе дефолтную метку операции.
 *
 *   - юзер ввёл title → используем его
 *   - есть source.title → «{opLabel} — {source.title}»
 *   - иначе → просто opLabel («Vocals», «Cover», «Instrumental»)
 *
 * Обрезаем до 100 символов (ограничение Suno API).
 */
function resolveParamTitle(
	userTitle: string | undefined,
	sourceTitle: string | undefined | null,
	opLabel: string,
): string {
	const u = (userTitle || '').trim()
	if (u) return u.slice(0, 100)
	const s = (sourceTitle || '').trim()
	if (s) return `${opLabel} — ${s}`.slice(0, 100)
	return opLabel
}

// ── Polling ──────────────────────────────────────────────────
export async function pollForResult(
	requestId: string,
	modelId: string,
	onSave?: (taskId: string, modelId: string) => void,
): Promise<string> {
	onSave?.(requestId, modelId)
	// Ждём ответа сервера, сколько бы ни шла генерация (waitForTask). Долгую можно увести в фон — виджет
	// освободится, а стикер дождётся результата.
	const s = await waitForTask(requestId, { isDone: (st) => st.status === 'completed' && !!st.result_url })
	if (!s || s.status === 'failed') {
		throw new Error(s?.error_message_ru || s?.error_message || 'Генерация не удалась')
	}
	return s.result_url as string
}

// Поллинг аудио — возвращает массив треков + обложек (parallel arrays). Без потолка, как и всё (waitForTask):
// прежние 40 опросов × 10 с объявляли «Таймаут генерации музыки», пока Suno досчитывал.
export async function pollForAudioResult(
	requestId: string,
	modelId: string,
	onSave?: (taskId: string, modelId: string) => void,
): Promise<{ urls: string[]; imageUrls: string[] }> {
	onSave?.(requestId, modelId)
	const urlsOf = (st: TaskStatusResponse) => st.result_urls || (st.result_url ? [st.result_url] : [])
	const s = await waitForTask(requestId, {
		minDelayMs: AUDIO_POLL_INTERVAL_MS,
		isDone: (st) => st.status === 'completed' && urlsOf(st).length > 0,
	})
	if (!s || s.status === 'failed') {
		throw new Error(s?.error_message_ru || s?.error_message || 'Генерация не удалась')
	}
	const urls = urlsOf(s)
	const imageUrls = await resolveAudioImageUrls(urls, s.image_urls || [])
	return { urls, imageUrls }
}

/**
 * Резолвим массив обложек треков (index-aligned с `urls`).
 *
 * Приоритет — что пришло от бэка в `TaskStatusResponse.image_urls`. Если там
 * пусто (старый бэк без джойна на suno_tracks, см. `_get_audio_image_urls`
 * в tasks.py) — fallback: дёргаем `GET /music/tracks` и сопоставляем по
 * audio_url. Фронт берёт обложки только для треков, которые совпадают с
 * только что сгенерированными (по URL), чтобы не подставить чужие.
 *
 * Работает как "помощник бэку" — когда бэк вернёт image_urls сам, fallback
 * не сработает (массив уже заполнен). После деплоя backend-правок эта
 * функция становится no-op в обычном сценарии.
 */
export async function resolveAudioImageUrls(
	urls: string[],
	fromBackend: string[],
): Promise<string[]> {
	// Бэк вернул осмысленные обложки — используем как есть.
	if (fromBackend.length >= urls.length && fromBackend.some((u) => !!u)) {
		return fromBackend
	}
	try {
		// Динамический импорт чтобы не тянуть getUserTracks в этот модуль
		// статически (он уже есть в bundle — просто чистим деп-граф).
		const { getUserTracks } = await import('../../../api/music')
		const res = await getUserTracks({ limit: 10 })
		const tracks = res.tracks || []
		// Параллельный массив по urls: для каждого audio URL ищем совпадающий
		// трек и достаём его image_url. Если не найден — пустая строка
		// (компонент покажет fallback-иконку в карточке).
		return urls.map((url) => {
			const found = tracks.find(
				(t) => t.audio_url === url || t.stream_audio_url === url,
			)
			return found?.image_url || ''
		})
	} catch {
		// Сеть сбойнула — возвращаем что было (может пустой массив).
		return fromBackend
	}
}

// ── Dispatch ─────────────────────────────────────────────────
interface RunGenerationParams {
	model: Model
	prompt: string
	settings: GenerateSettings
	uploadedImage: UploadedFile | null
	swapImage: UploadedFile | null
	uploadedVideo: UploadedVideo | null
	referenceVideo: UploadedVideo | null
	referenceImages: UploadedFile[]
	endImage: UploadedFile | null
	omniReferenceImages: UploadedFile[]
	referenceVideos?: UploadedVideo[]
	referenceAudios?: UploadedFile[]
	multiShots: { prompt: string; duration: number }[]
	selectedVideoDuration: number
	requiresVideo: boolean
	requiresTwoImages: boolean
	supportsMultipleImages: boolean
	onPendingSave: (taskId: string, modelId: string) => void
	/** Audio-only: если задан — делаем операцию над существующим треком. */
	sourceTrack?: SunoTrack | null
	audioOperation?: AudioOperation
	/** Демо-трек прикреплённой карточки — стилевой референс НОВОЙ генерации.
	 *  Работает только на Mureka (гейт внутри runner'а); для Suno и операций
	 *  над треками тихо игнорируется — генерация идёт обычным путём. */
	audioReferenceUrl?: string
	/** Цена на кнопке в момент запуска: у видео с платой за секунды входа сервер не спишет больше (input_video_billing). */
	expectedCredits?: number | null
}

async function resolveResponse(
	response: ApiResponse,
	isVideoResult: boolean,
	onPendingSave: (taskId: string, modelId: string) => void,
	modelId: string,
): Promise<GenerationResult> {
	if (!response.ok) {
		// Отказ по правилам («Отказы нейросетей»): окно покажет RefusalNoticeHost, здесь — текст карточки.
		if (response.refusal?.action === 'warn' || response.error === 'NSFW_WARNING') return { ok: false, isNsfwWarning: true }
		if (response.refusal?.action === 'charge' || response.error === 'NSFW_CHARGED') {
			return { ok: false, error: response.refusal?.text || 'Запрос заблокирован модерацией. Токены списаны без возврата.' }
		}
		return { ok: false, error: response.error || 'Ошибка генерации' }
	}
	// Подпись сервера к запуску (длина по референсу, перезапуск как редактирование):
	// раньше терялась — юзер узнавал о ней из результата.
	if (response.notice) showToast(response.notice, 'info')

	const resultUrl = response.video_url || response.image_url
	if (resultUrl) {
		return { ok: true, resultUrl, requestId: response.request_id }
	}

	if (response.request_id) {
		const url = await pollForResult(response.request_id, modelId, onPendingSave)
		return { ok: true, resultUrl: url, requestId: response.request_id }
	}

	return { ok: false, error: 'Нет результата от сервера' }
}

export async function runGeneration(params: RunGenerationParams): Promise<GenerationResult> {
	const {
		model,
		prompt,
		settings,
		uploadedImage,
		swapImage,
		uploadedVideo,
		referenceVideo,
		referenceImages,
		endImage,
		omniReferenceImages,
		referenceVideos,
		referenceAudios,
		multiShots,
		selectedVideoDuration,
		requiresVideo,
		requiresTwoImages,
		supportsMultipleImages,
		onPendingSave,
		expectedCredits,
	} = params

	// ── Озвучка (ElevenLabs) ─────────────────────────────────────
	// Модель озвучки умеет только это — даже если режим виджета не успел
	// переключиться, песню ею не запускаем. СИНХРОННО: ручка сама кладёт mp3 к
	// нам и создаёт трек озвучки (operation='tts'), поллинг не нужен.
	if (model.category === 'audio' && isTtsModelId(model.id)) {
		const speech = await generateSpeech({
			text: prompt,
			// Пусто/устаревший id (например, «Ethan» времён Mureka) — бэк возьмёт голос по умолчанию.
			voice_id: settings.audioVoice || undefined,
			model: model.id,
		})
		if (!speech.ok) return { ok: false, error: speech.error || 'Не удалось озвучить текст' }
		return {
			ok: true,
			resultUrl: speech.audio_url || '',
			resultUrls: speech.audio_url ? [speech.audio_url] : [],
			requestId: speech.request_id,
		}
	}

	// ── Audio (Suno) ─────────────────────────────────────────────
	if (model.category === 'audio') {
		const { sourceTrack, audioOperation } = params
		const apiModel = sunoModelApiId(model.id)
		let res: MusicGenerateResponse

		if (sourceTrack && audioOperation && audioOperation !== 'generate') {
			// Параметрическая операция над существующим треком.
			// Все они возвращают тот же shape {ok, request_id} и опрашиваются
			// через pollForAudioResult (результат — массив новых треков).
			switch (audioOperation) {
				case 'extend':
					res = await extendTrack({
						track_id: sourceTrack.id,
						model: apiModel,
						prompt,
						style: settings.audioStyle || '',
						title: settings.audioTitle?.trim() || '',
						negative_tags: settings.audioNegativeTags?.trim() || '',
						vocal_gender: settings.audioVocalGender || '',
						// Гайд §5: continue_at — с какой секунды продолжить. 0 = с конца.
						continue_at: settings.audioContinueAt && settings.audioContinueAt > 0
							? settings.audioContinueAt
							: undefined,
						persona_id: settings.audioPersonaId || undefined,
					})
					break
				case 'cover': {
					// Cover-версия по гайду §17 — `upload-cover` с track_id.
					//
					// КРИТИЧНО: Suno работает в двух режимах:
					//   custom_mode=true  + prompt=lyrics  → генерит С ТЕКСТОМ
					//   custom_mode=true  + prompt=пусто   → ломается (инструментал/хаос)
					//   custom_mode=false + prompt=пусто   → Suno сам пишет текст под style
					//
					// Раньше мы слали {custom_mode: true, prompt: ''} — получался
					// трек без текста. Теперь логика такая:
					//   1. Если юзер ввёл свой prompt → custom_mode=true, prompt=его
					//   2. Если не ввёл, но у оригинала есть lyrics → сохраняем текст
					//      оригинала (custom_mode=true, prompt=sourceTrack.lyrics)
					//   3. Если ни prompt'а, ни lyrics у оригинала → custom_mode=false,
					//      Suno сам напишет текст в новом жанре.
					// Инструментал и добровольно-false custom_mode переопределяют это.
					const userPrompt = prompt.trim()
					const originalLyrics = sourceTrack.lyrics?.trim() || ''
					const wantsInstrumental = !!settings.audioInstrumental
					const userCustomMode = settings.audioCustomMode ?? true

					let finalPrompt: string
					let finalCustomMode: boolean

					if (wantsInstrumental) {
						// Инструментал — текст не нужен, режим не важен
						finalPrompt = userPrompt
						finalCustomMode = userCustomMode
					} else if (userPrompt) {
						// Юзер ввёл свой текст — используем
						finalPrompt = userPrompt
						finalCustomMode = true
					} else if (!userCustomMode) {
						// Юзер явно выключил custom-mode → Suno сам напишет
						finalPrompt = ''
						finalCustomMode = false
					} else if (originalLyrics) {
						// Сохраняем текст оригинала
						finalPrompt = originalLyrics
						finalCustomMode = true
					} else {
						// Нет ни prompt'а ни текста оригинала → Suno сам напишет
						finalPrompt = ''
						finalCustomMode = false
					}

					res = await uploadCover({
						track_id: sourceTrack.id,
						model: apiModel,
						prompt: finalPrompt,
						style: settings.audioStyle || '',
						title: resolveParamTitle(settings.audioTitle, sourceTrack.title, 'Cover'),
						custom_mode: finalCustomMode,
						instrumental: wantsInstrumental,
						negative_tags: settings.audioNegativeTags?.trim() || '',
						vocal_gender: settings.audioVocalGender || '',
					})
					break
				}
				case 'add_vocals':
					// Гайд §8: add-vocals требует prompt/style/title/negative_tags/vocal_gender.
					// Suno отклоняет запрос с пустым title — подставляем fallback
					// («Vocals» или название source-трека).
					res = await addVocals({
						track_id: sourceTrack.id,
						model: apiModel,
						prompt,
						style: settings.audioStyle || '',
						title: resolveParamTitle(settings.audioTitle, sourceTrack.title, 'Vocals'),
						negative_tags: settings.audioNegativeTags?.trim() || '',
						vocal_gender: settings.audioVocalGender || '',
						persona_id: settings.audioPersonaId || undefined,
					})
					break
				case 'add_instrumental':
					// Гайд §7: add-instrumental требует tags (описание инструментала) +
					// negative_tags как обязательные. `tags` маппится на audioStyle — это поле
					// в UI уже отвечает за «описание жанра/инструментала».
					res = await addInstrumental({
						track_id: sourceTrack.id,
						model: apiModel,
						tags: settings.audioStyle || '',
						negative_tags: settings.audioNegativeTags?.trim() || '',
						title: resolveParamTitle(settings.audioTitle, sourceTrack.title, 'Instrumental'),
						vocal_gender: settings.audioVocalGender || '',
						// Конкретная партия (только Mureka): пусто = вся музыка, как было.
						track_type: settings.audioTrackType || '',
					})
					break
			}
		} else if (settings.audioSubmode === 'sounds') {
			// SFX режим — другой эндпоинт, без title/style/vocals.
			res = await generateSounds({
				prompt,
				sound_loop: settings.soundLoop ?? false,
				sound_tempo: settings.soundTempo,
				sound_key: settings.soundKey,
			})
		} else if (settings.audioSubmode === 'soundtrack') {
			// Soundtrack (Mureka): фото/видео → музыка. media_url из загрузки.
			const stMedia = uploadedVideo?.url
				? { url: uploadedVideo.url, isVideo: true }
				: uploadedImage?.url
					? { url: uploadedImage.url, isVideo: false }
					: null
			if (!stMedia) return { ok: false, error: 'Загрузите фото или видео для саундтрека' }
			res = await generateSoundtrack({
				media_url: stMedia.url,
				is_video: stMedia.isVideo,
				model: apiModel,
				prompt,
				title: settings.audioTitle?.trim() || 'Soundtrack',
				style: settings.audioStyle || '',
				// Кусок ролика (только для видео): пусто = весь ролик, как было.
				...(stMedia.isVideo && settings.soundtrackStartSec != null && settings.soundtrackEndSec != null
					? {
						audio_start_sec: settings.soundtrackStartSec,
						audio_end_sec: settings.soundtrackEndSec,
					}
					: {}),
			})
		} else if (settings.audioSubmode === 'tts') {
			// Озвучка — только моделью озвучки (ветка выше). Музыкальная модель
			// сюда попасть не должна (виджет сбрасывает режим), но если попала —
			// не запускаем песню вместо озвучки.
			return { ok: false, error: 'Озвучку делает ElevenLabs — откройте вкладку «Озвучка»' }
		} else if (params.audioReferenceUrl && providerOf(model.id) === 'mureka') {
			// Референс по демо-треку прикреплённой карточки (только Mureka):
			// бэк /music/reference заливает аудио в Mureka (purpose=reference) и
			// генерит НОВУЮ песню в его звучании. Цена и поллинг — как у обычной
			// генерации (тот же request_id → pollForAudioResult ниже).
			res = await referenceGenerate({
				upload_url: params.audioReferenceUrl,
				model: apiModel,
				prompt,
				style: settings.audioStyle || 'Pop',
				title: settings.audioTitle?.trim() || 'Generated Track',
				instrumental: settings.audioInstrumental || false,
				custom_mode: settings.audioCustomMode ?? true,
				// Инструментал = трек без вокала: пол и персона в запрос не идут,
				// что бы ни осталось в настройках (UI их только пригашает).
				vocal_gender: settings.audioInstrumental ? '' : settings.audioVocalGender || '',
				persona_id: settings.audioInstrumental ? undefined : settings.audioPersonaId || undefined,
			})
		} else {
			res = await generateMusicAsync({
				prompt,
				style: settings.audioStyle || 'Pop',
				title: settings.audioTitle?.trim() || 'Generated Track',
				model: apiModel,
				instrumental: settings.audioInstrumental || false,
				custom_mode: settings.audioCustomMode ?? true,
				// Инструментал = трек без вокала: пол и персона в запрос не идут.
				vocal_gender: settings.audioInstrumental ? '' : settings.audioVocalGender || '',
				negative_tags: settings.audioNegativeTags?.trim() || '',
				persona_id: settings.audioInstrumental ? undefined : settings.audioPersonaId || undefined,
			})
		}

		if (!res.ok) return { ok: false, error: res.error || 'Ошибка генерации' }
		if (!res.request_id) return { ok: false, error: 'Нет request_id от сервера' }
		try {
			const { urls, imageUrls } = await pollForAudioResult(res.request_id, model.id, params.onPendingSave)
			return {
				ok: true,
				resultUrl: urls[0],
				resultUrls: urls,
				imageUrls,
				requestId: res.request_id,
			}
		} catch (e) {
			return { ok: false, error: e instanceof Error ? e.message : 'Ошибка генерации' }
		}
	}

	const imageInput = getImageInput({
		requiresTwoImages,
		supportsMultipleImages,
		uploadedImage,
		swapImage,
		referenceImages,
	})

	const isVideoResult = model.category === 'video'
	let response: ApiResponse

	// ── Midjourney ──────────────────────────────────────────────
	if (model.provider === PROVIDER.MIDJOURNEY) {
		if (model.taskType === TASK_TYPE.I2V) {
			response = await generateMidjourneyVideo(buildMidjourneyVideoPayload(prompt, uploadedImage!))
		} else if (model.taskType === TASK_TYPE.I2I) {
			response = await imageToImage(buildMidjourneyI2IPayload(prompt, uploadedImage!, settings))
		} else {
			response = await generateMidjourney(buildMidjourneyPayload(prompt, settings, model))
		}
	}
	// ── Kling 2.6 / avatar / motion-control ──────────────────────
	// Kling 3.0 через KIE (provider тоже `kling`) сюда НЕ идёт: buildKlingPayload
	// знает только фото и видео, а у 3.0 есть последний кадр и мультишот —
	// они молча пропадали (тестировщик 14.09: «загруженный файл не
	// используется как кадр»). Общий buildVideoPayload собирает для kling-3
	// кадры и multi_shots, как у прямой версии.
	else if (model.provider === PROVIDER.KLING && !isKling3Model(model)) {
		response = await generateVideo(
			buildKlingPayload(
				prompt,
				settings,
				model,
				uploadedImage,
				uploadedVideo,
				selectedVideoDuration,
				requiresVideo,
			),
		)
	}
	// ── Generic video ────────────────────────────────────────────
	else if (model.category === 'video') {
		// «Переделать видео» работает с одним роликом — стоп до запроса (сервер проверит ещё раз).
		const intentProblem = videoIntentProblem(model, settings, referenceVideos ?? [])
		if (intentProblem) return { ok: false, error: intentProblem }
		response = await generateVideo(
			buildVideoPayload(
				prompt,
				settings,
				model,
				imageInput,
				uploadedVideo,
				referenceVideo,
				endImage,
				omniReferenceImages,
				multiShots,
				selectedVideoDuration,
				requiresVideo,
				referenceVideos,
				referenceAudios,
				expectedCredits,
			),
		)
	}
	// ── Flux ─────────────────────────────────────────────────────
	else if (model.provider === PROVIDER.FLUX) {
		response = await generateImageAsync(buildFluxPayload(prompt, settings, model, imageInput))
	}
	// ── Generic image ────────────────────────────────────────────
	else if (model.category === 'image') {
		response = await generateImageAsync(buildImagePayload(prompt, settings, model, imageInput))
	}
	// ── Presentation ────────────────────────────────────────────
	else if (model.category === 'presentation') {
		const pres = settings.presentation
		if (!pres) {
			return { ok: false, error: 'Не задан режим презентации' }
		}
		if (!prompt.trim()) {
			return { ok: false, error: 'Опишите тему презентации' }
		}

		const backendModel = model.backendModel || model.id
		const isGamma = backendModel === 'gamma-presentation'

		// ── Gamma: отдельный эндпоинт, только create-режим ────────
		if (isGamma) {
			const { generateGammaPresentation } = await import('../../../api/presentations')
			const res = await generateGammaPresentation({
				prompt: prompt.trim(),
				// Открытая папка страницы — готовая презентация сразу ляжет в неё.
				folder_id: getSlidesFolder() || undefined,
				num_cards: pres.gammaNumCards ?? 5,
				language: pres.gammaLanguage ?? 'ru',
				image_source: pres.gammaImageSource ?? 'aiGenerated',
				dimensions: pres.gammaDimensions ?? '16x9',
				theme_id: pres.gammaThemeId || null,
				additional_instructions: pres.gammaAdditionalInstructions || undefined,
				text_amount: pres.gammaTextAmount ?? 'medium',
				export_as: pres.gammaExportAs ?? 'pptx',
				image_model: pres.gammaImageModel || undefined,
				image_style: pres.gammaImageStyle || undefined,
				text_mode: pres.gammaTextMode ?? 'generate',
				// Колонтитулы: карта 6 слотов (+ легаси-конвертация внутри).
				...hfPayloadFromSettings(pres),
				// tone и audience опциональные — пустые не отправляем чтобы Gamma
				// сама выбрала default'ы.
				tone: pres.gammaTone || undefined,
				audience: pres.gammaAudience || undefined,
			})

			if (!res.ok || !res.request_id) {
				return { ok: false, error: res.error || 'Ошибка запуска генерации' }
			}
			try {
				const resultUrl = await pollForResult(res.request_id, model.id, params.onPendingSave)
				return { ok: true, resultUrl, requestId: res.request_id }
			} catch (e) {
				return { ok: false, error: e instanceof Error ? e.message : 'Ошибка генерации' }
			}
		}

		// ── Umnik Slides (Claude) ─────────────────────────────────
		if (pres.mode === 'edit' && !pres.fileUrl) {
			return { ok: false, error: 'Загрузите PPTX файл' }
		}
		if (pres.mode === 'create' && !pres.template && !pres.customTemplateUrl) {
			return { ok: false, error: 'Выберите шаблон или загрузите свой' }
		}

		const { generatePresentation } = await import('../../../api/presentations')
		const res = await generatePresentation({
			folder_id: getSlidesFolder() || undefined,
			model: backendModel,
			mode: pres.mode,
			prompt: prompt.trim(),
			file_url: pres.mode === 'edit' ? pres.fileUrl : undefined,
			template: pres.mode === 'create' ? pres.template : undefined,
			custom_template_url: pres.mode === 'create' ? pres.customTemplateUrl : undefined,
			slide_count: pres.mode === 'create' ? pres.slideCount : undefined,
			image_urls: pres.imageUrls?.length ? pres.imageUrls : undefined,
			image_filenames: pres.imageFilenames?.length ? pres.imageFilenames : undefined,
		})

		if (!res.ok || !res.request_id) {
			return { ok: false, error: res.error || 'Ошибка запуска генерации' }
		}

		try {
			const resultUrl = await pollForResult(res.request_id, model.id, params.onPendingSave)
			return { ok: true, resultUrl, requestId: res.request_id }
		} catch (e) {
			return { ok: false, error: e instanceof Error ? e.message : 'Ошибка генерации' }
		}
	} else {
		return { ok: false, error: 'Неизвестный провайдер' }
	}

	return resolveResponse(response, isVideoResult, onPendingSave, model.id)
}
